interface GraphQLErrorLike {
    message: string;
    extensions?: Record<string, unknown>;
}

interface NetworkErrorLike {
    statusCode?: unknown;
    response?: {
        status?: unknown;
    };
}

export interface AuthenticationErrorInput {
    graphQLErrors?: ReadonlyArray<GraphQLErrorLike>;
    networkError?: unknown;
}

export interface SessionInvalidationInput extends AuthenticationErrorInput {
    operationName: string;
    hasAuthToken: boolean;
}

interface SessionInvalidatorDependencies {
    getAuthToken: () => string | null;
    removeAuthToken: () => void;
    markLoggedOut: () => void;
    redirectToLogin: () => void;
}

export const AUTH_TOKEN_STORAGE_KEY = "authToken";
export const USER_LOGIN_OPERATION_NAME = "UserLogin";

const isUnauthenticatedGraphQLError = ({
    message,
    extensions,
}: GraphQLErrorLike): boolean =>
{
    const code = extensions?.code;

    if (code === "UNAUTHENTICATED") {
        return true;
    }

    return message.trim() === "Unauthenticated.";
};

const getNetworkStatusCode = (networkError: unknown): number | undefined =>
{
    if (!networkError || typeof networkError !== "object") {
        return undefined;
    }

    const {
        statusCode,
        response,
    } = networkError as NetworkErrorLike;

    if (typeof statusCode === "number") {
        return statusCode;
    }

    return typeof response?.status === "number" ? response.status : undefined;
};

export const isUnauthenticatedError = ({
    graphQLErrors = [],
    networkError,
}: AuthenticationErrorInput): boolean =>
    graphQLErrors.some(isUnauthenticatedGraphQLError)
    || getNetworkStatusCode(networkError) === 401;

export const shouldInvalidateSession = ({
    operationName,
    hasAuthToken,
    graphQLErrors,
    networkError,
}: SessionInvalidationInput): boolean =>
    hasAuthToken
    && operationName !== USER_LOGIN_OPERATION_NAME
    && isUnauthenticatedError({ graphQLErrors, networkError });

export const createSessionInvalidator = ({
    getAuthToken,
    removeAuthToken,
    markLoggedOut,
    redirectToLogin,
}: SessionInvalidatorDependencies): (() => boolean) =>
{
    let invalidationStarted = false;

    return (): boolean =>
    {
        if (invalidationStarted || !getAuthToken()) {
            return false;
        }

        invalidationStarted = true;
        removeAuthToken();
        markLoggedOut();
        redirectToLogin();

        return true;
    };
};
